import React, { useState, useEffect } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
    Flex,
    Box,
    Text,
    Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
    EllipsisVertical,
    Plus,
    Pencil,
    Archive,
    ShieldAlert,
    MessageSquare,
    EyeClosed,
    EyeIcon,
} from "lucide-react"; // Import relevant icons
import GradientButton from "components/Buttons/GradientButton";
// import AddVehicleDrawer from "./components/AddVehicleDrawer";
import ViewVehicle from "./components/ViewVehicle";
import EditVehicleDrawer from "./components/EditVehicleDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVehiclesAction } from '../../../state/actions/vehicleActions';
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from 'components/Toasts/OpenNotification.js';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';

const VehicleManagement = () => {
    const [isAddVehicleDrawerOpen, setAddVehicleDrawerOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isViewVehicleDrawerOpen, setViewVehicleDrawerOpen] = useState(false);
    const [isEditVehicleDrawerOpen, setEditVehicleDrawerOpen] = useState(false);
    const [isArchiveVehicleDialogOpen, setArchiveVehicleDialogOpen] = useState(false);
    const [selectedVehicleForArchive, setSelectedVehicleForArchive] = useState(null);
    const history = useHistory();
    const showToast = useCustomNotification();
    const dispatch = useDispatch();
    const { vehicles, loading, error } = useSelector((state) => state.vehicles);

    const handleOpenArchiveVehicleDialog = (vehicle) => {
        setSelectedVehicleForArchive(vehicle);
        setArchiveVehicleDialogOpen(true);
    };

    const handleCloseArchiveVehicleDialog = () => {
        setSelectedVehicleForArchive(null);
        setArchiveVehicleDialogOpen(false);
    };

    const handleArchiveVehicle = () => {
        console.log("Archiving vehicle:", selectedVehicleForArchive);
    };

    const handleOpenAddVehicleDrawer = () => {
        setAddVehicleDrawerOpen(true);
    };

    const handleCloseAddVehicleDrawer = () => {
        setAddVehicleDrawerOpen(false);
    };

    const handleAddVehicle = async (newVehicle) => {
        // Assuming you have an action for adding a vehicle
        const response = await dispatch(registerVehicleAction(newVehicle));

        if (response.success) {
            showToast({
                type: "success",
                message: "Vehicle added successfully",
            });
            handleCloseAddVehicleDrawer();
        } else {
            showToast({
                type: "error",
                message: "Registration Failed",
                description: response.error.split('\n').map(msg => ` ${msg}`).join('\n'),
            });
        }
    };

    const handleViewVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setViewVehicleDrawerOpen(true);
    };

    const handleCloseViewVehicleDrawer = () => {
        setSelectedVehicle(null);
        setViewVehicleDrawerOpen(false);
    };

    const handleOpenEditVehicleDrawer = (vehicle) => {
        setSelectedVehicle(vehicle);
        setEditVehicleDrawerOpen(true);
    };

    const handleCloseEditVehicleDrawer = () => {
        setSelectedVehicle(null);
        setEditVehicleDrawerOpen(false);
    };

    const generateMenuItems = (row) => [
        {
            icon: <EyeIcon size={16} />,
            text: "View Vehicle",
            onClick: () => handleViewVehicle(row.original),
        },
        {
            icon: <MessageSquare size={16} />,
            text: "Chat with Owner",
            onClick: () => history.push('/admin/messages'),
        },
        {
            icon: <Pencil size={16} />,
            text: "Update Vehicle Status",
            onClick: () => handleOpenEditVehicleDrawer(row.original),
        },
    ];

    useEffect(() => {
        dispatch(fetchVehiclesAction());
    }, [dispatch]);

    const statusColorScheme = {
        Approved: "green",
        Pending: "gray",
        Suspended: "red",
        Rejected: "red",
        Deactivated: "gray",
    };


    // Define the columns for the table
    const columnsData = [
        {
            Header: "Owner",
            accessor: "user_info",
            Cell: ({ value }) => {
                // Check if first_name and last_name exist, if not, fallback to username
                const fullName = value && value.first_name && value.last_name
                    ? `${value.first_name} ${value.last_name}`
                    : value && value.username
                        ? value.username
                        : 'No owner information';

                return <Text>{fullName}</Text>;
            },
        },
        {
            Header: "Vehicle Model",
            accessor: "vehicle.vehicle_model",
        },
        {
            Header: "License Plate",
            accessor: "vehicle.registration_number",
        },
        {
            Header: "Type",
            accessor: "vehicle.vehicle_make",
        },
        {
            Header: "Status",
            accessor: "vehicle.status",
            Cell: ({ value }) => (
                <Badge
                    colorScheme={statusColorScheme[value]}
                    borderRadius="full"
                    px={2}
                >
                    {value}
                </Badge>
            ),
        },
        {
            Header: "Location",
            accessor: "user_info.village",
        },
        {
            Header: "Date Added",
            accessor: "vehicle.created_at",
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: "Actions",
            accessor: "moreOptions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
            <Box p={6}>
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="md" fontWeight="bold">
                        {loading ? (
                            <Skeleton height="20px" width="100px" />
                        ) : (
                            `All Vehicles: ${vehicles?.total ?? 0}`
                        )}
                    </Text>
                    {/* 
                    {loading ? (
                        <Skeleton height="40px" width="120px" />
                    ) : (
                        <GradientButton
                            icon={<Plus size={16} />}
                            // onClick={handleOpenAddVehicleDrawer}
                            height={"40px"}
                        >
                            Add Vehicle
                        </GradientButton>
                    )} */}
                </Flex>
            </Box>
            <ReusableTable
                columnsData={columnsData}
                tableData={Array.isArray(vehicles?.data) ? vehicles.data : []}
                isLoading={loading}
            />


            {/* View Vehicle Drawer */}
            <ReusableDrawer
                isOpen={isViewVehicleDrawerOpen}
                onClose={handleCloseViewVehicleDrawer}
                headerContent={<h2 style={{ fontSize: "md" }}>View Vehicle</h2>}
                bodyContent={
                    selectedVehicle && (
                        <ViewVehicle
                            vehicle={selectedVehicle}
                            onClose={handleCloseViewVehicleDrawer}
                        />
                    )
                }
            />


            <EditVehicleDrawer
                isOpen={isEditVehicleDrawerOpen}
                onClose={handleCloseEditVehicleDrawer}
                vehicle={selectedVehicle}
            />

            {/* <AddVehicleDrawer
        isOpen={isAddVehicleDrawerOpen}
    onClose={handleCloseAddVehicleDrawer}
        onAddVehicle={handleAddVehicle}
      /> */}

        </Flex>
    );
};

export default VehicleManagement;
